import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  TemplateRef,
} from '@angular/core';
import { ButtonModule } from '@openng/optimus-ui/button';
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogModule,
  DynamicDialogRef,
} from '@openng/optimus-ui/dynamicdialog';
import {
  CommandOptions,
  CustomButtonOption,
  CustomButtonTemplate,
  CustomDialogOption,
} from './custom-dialog.component';

@Component({
  selector: 'core-custom-dialog-content',
  providers: [DialogService],
  imports: [CommonModule, DynamicDialogModule, ButtonModule],
  template: ` @if (title || titleTemplate) {
      <div data-testid="customTitle">
        @if (titleTemplate) {
          <ng-container
            [ngTemplateOutlet]="titleTemplate"
            [ngTemplateOutletContext]="{ option: option }"
          >
          </ng-container>
        }
        @if (title) {
          {{ title }}
        }
      </div>
    }
    <div data-testid="customContent">
      <ng-container>
        @if (contentTemplate) {
          <ng-container
            [ngTemplateOutlet]="contentTemplate"
            [ngTemplateOutletContext]="{
              option: option,
              data: option?.contentData ?? null,
              dialog: ref,
            }"
          >
          </ng-container>
        }
        @if (content) {
          {{ content }}
        }
      </ng-container>
    </div>
    <div class="flex justify-end gap-2 mt-4" data-testid="customButtons">
      @for (buttonTemplate of buttonTemplates; track $index) {
        @switch (buttonTemplate.template) {
          @case ('command') {
            <p-button
              [disabled]="
                $safeNavigationMigration(
                  buttonTemplate.option.disabled?.(
                    $safeNavigationMigration(option?.contentData)
                  )
                )
              "
              label="{{ buttonTemplate.option.text }}"
              class="text-sm"
              (click)="
                onCommand(
                  buttonTemplate.option,
                  $safeNavigationMigration(option?.contentData)
                )
              "
              data-testid="btnCommand"
            />
          }
          @case ('ok') {
            <p-button
              [label]="buttonTemplate.option.text ?? defaultOkLabel"
              class="text-sm"
              (click)="onOk()"
              data-testid="btnOK"
            />
          }
          @case ('cancel') {
            <p-button
              [label]="buttonTemplate.option.text ?? defaultCancelLabel"
              class="text-sm"
              (click)="onCancel()"
              data-testid="btnCancel"
              severity="secondary"
            />
          }
        }
      }
    </div>`,
  styles: [``],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomDialogContentComponent {
  protected readonly defaultOkLabel = $localize`:@@alert-dialog-btn-ok:OK`;
  protected readonly defaultCancelLabel = $localize`:@@alert-dialog-btn-cancel:Cancel`;

  protected title?: string;
  protected titleTemplate?: TemplateRef<unknown>;
  protected content?: string;
  protected contentTemplate?: TemplateRef<unknown>;
  protected buttonTemplates: CustomButtonTemplate<unknown>[] = [];
  protected option?: CustomDialogOption;
  protected readonly config = inject(DynamicDialogConfig<CustomDialogOption>);
  protected readonly ref = inject(DynamicDialogRef);

  public constructor() {
    this.option = this.config.data;
    if (this.option) {
      if (this.option.title && typeof this.option.title == 'string') {
        this.title = this.option.title;
      } else if (typeof this.option.title == 'object') {
        this.titleTemplate = this.option.title;
      }
      if (this.option.content && typeof this.option.content == 'string') {
        this.content = this.option.content;
      } else if (typeof this.option.content == 'object') {
        this.contentTemplate = this.option.content;
      }
      if (this.option.buttons) {
        this.buttonTemplates = this.option.buttons.map<
          CustomButtonTemplate<unknown>
        >(btn => ({
          template: btn.type ?? 'ok',
          option: btn,
        }));
      } else {
        this.buttonTemplates = [
          {
            template: 'ok',
            option: {},
          },
        ];
      }
    }
  }

  protected onOk() {
    this.ref.close(true);
  }

  protected onCancel() {
    this.ref.close(false);
  }

  protected async onCommand(option: CustomButtonOption, data: unknown) {
    if (option.command) {
      const options: CommandOptions = { autoClose: true };
      const result = await option.command(data, options);
      if (options.autoClose) this.ref.close(result);
    } else {
      this.ref.close(true);
    }
  }
}
